import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * The mobile wrapper reports an in-app purchase here. The receipt is verified
 * against Apple/Google before anything is written, and the row is always
 * attributed to the authenticated caller — never to a client-supplied user id.
 */
export const linkStorePurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { platform: "apple" | "google"; receipt: string; productId?: string }) => {
    if (data.platform !== "apple" && data.platform !== "google") throw new Error("Unknown store platform");
    if (typeof data.receipt !== "string" || data.receipt.length < 8 || data.receipt.length > 20_000) {
      throw new Error("Invalid receipt");
    }
    return data;
  })
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const store = await import("@/lib/store-billing.server");

    if (data.platform === "apple") {
      // The wrapper sends the signed transaction JWS from StoreKit 2.
      const tx = await store.verifyAppleJws<{
        originalTransactionId?: string;
        productId?: string;
        environment?: string;
        appAccountToken?: string;
      }>(data.receipt);
      if (!tx?.originalTransactionId) return { ok: false, error: "receipt_invalid" as const };

      const env = (tx.environment ?? "Production").toLowerCase() === "sandbox" ? "sandbox" : "production";
      const status = await store.fetchAppleSubscriptionStatus(tx.originalTransactionId, env);
      if (!status) return { ok: false, error: "store_not_configured" as const };

      const record = store.appleRecord({
        userId,
        transaction: status.transaction,
        renewal: status.renewal,
        statusCode: status.status,
      });
      if (!record) return { ok: false, error: "receipt_invalid" as const };
      if (await store.isStoreTransactionOwnedByOther(record.platform, record.transactionRef, userId)) {
        return { ok: false, error: "already_linked" as const };
      }

      const result = await store.persistStoreSubscription(
        record,
        record.isTrial ? "trial_started" : "started",
        "Purchase linked from the iOS app",
      );
      return { ok: result.ok, status: record.status, expiresAt: record.expiresAt, error: null };
    }

    const sub = await store.fetchPlaySubscription(data.receipt);
    if (!sub) return { ok: false, error: "store_not_configured" as const };
    const record = store.playRecord(userId, data.receipt, sub);
    if (!record) return { ok: false, error: "receipt_invalid" as const };
    if (await store.isStoreTransactionOwnedByOther(record.platform, record.transactionRef, userId)) {
      return { ok: false, error: "already_linked" as const };
    }

    const result = await store.persistStoreSubscription(
      record,
      record.isTrial ? "trial_started" : "started",
      "Purchase linked from the Android app",
    );
    // Acknowledge only after server verification shows an entitling state.
    if (
      result.ok &&
      sub.acknowledgementState === "ACKNOWLEDGEMENT_STATE_PENDING" &&
      (record.status === "active" || record.status === "trialing")
    ) {
      await store.acknowledgePlaySubscription(record.productId, data.receipt);
    }
    return { ok: result.ok, status: record.status, expiresAt: record.expiresAt, error: null };
  });

/**
 * "Restore purchases" for store billing: re-reads every store subscription we
 * already know about for this account and refreshes its status.
 */
export const refreshStoreSubscriptions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const store = await import("@/lib/store-billing.server");

    const { data: rows } = await supabase
      .from("store_subscriptions")
      .select("platform, transaction_ref, environment")
      .eq("user_id", userId);

    let refreshed = 0;
    for (const row of (rows ?? []) as Array<{ platform: string; transaction_ref: string; environment: string }>) {
      if (row.platform === "apple") {
        const status = await store.fetchAppleSubscriptionStatus(
          row.transaction_ref,
          row.environment === "sandbox" ? "sandbox" : "production",
        );
        if (!status) continue;
        const record = store.appleRecord({
          userId,
          transaction: status.transaction,
          renewal: status.renewal,
          statusCode: status.status,
        });
        if (!record) continue;
        await store.persistStoreSubscription(record, "updated");
        refreshed += 1;
      } else {
        const sub = await store.fetchPlaySubscription(row.transaction_ref);
        if (!sub) continue;
        const record = store.playRecord(userId, row.transaction_ref, sub);
        if (!record) continue;
        await store.persistStoreSubscription(record, "updated");
        refreshed += 1;
      }
    }

    const { data: state } = await supabase.rpc("my_premium_state");
    return {
      refreshed,
      premium: Boolean((state as { premium_access?: boolean } | null)?.premium_access),
    };
  });